/**
 * @fileoverview Defines React components `Favicon`, `OpenGraphDefault`, `OpenGraphTurbopuffer`, and `OpenGraphRime` to manage `<link>` and `<meta>` tags in the document head using React 19's native document metadata support. `Favicon` dynamically updates the favicon based on the user's preferred color scheme using `useState` and `useEffect`. The OpenGraph components set default and specific og:image and og:description meta tags.
 */

import React from 'react'
// No longer need react-helmet import

export const Favicon = () => {
  const [isDarkMode, setIsDarkMode] = React.useState(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
  )

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return (
    // Using direct link tag instead of Helmet wrapper
    <link
      rel="icon"
      type="image/png"
      href={isDarkMode ? '/images/favicon-dark.png' : '/images/favicon-light.png'}
    />
  )
}

export const OpenGraphDefault = () => {
  return (
    // Directly using metadata elements instead of Helmet wrapper
    <>
      <title>Ref.</title>
      <meta property="og:image" content="/images/ref-og-image.png" />
      <meta property="og:description" content="Documentation for your agent." />
    </>
  )
}

export const OpenGraphTurbopuffer = () => {
  return (
    // Directly using metadata elements instead of Helmet wrapper
    <>
      <title>Use case: Firestore → Turbopuffer</title>
      <meta property="og:image" content="/images/ref-turbopuffer-og-image.png" />
      <meta
        property="og:description"
        content="How we migrated from Firestore to Turbopuffer with Ref."
      />
    </>
  )
}

export const OpenGraphRime = () => {
  return (
    // Directly using metadata elements instead of Helmet wrapper
    <>
      <title>Use case: Rime MCP</title>
      <meta property="og:image" content="/images/ref-rime-og-image.png" />
      <meta property="og:description" content="How we build the Rime MCP server with Ref." />
    </>
  )
}

export const OpenGraphHowRefMcp = () => {
  return (
    // Directly using metadata elements instead of Helmet wrapper
    <>
      <meta
        property="og:title"
        content="How Ref uses MCP to build the best possible documentation search"
      />
      <meta property="og:image" content="/images/ref-mcp/ref-and-mcp-light.png" />
      <meta
        property="og:description"
        content="A look at how Ref takes advantage of different features of the Model Context Protocol."
      />
    </>
  )
}

export const OpenGraphHowMakeSearchGood = () => {
  return (
    // Directly using metadata elements instead of Helmet wrapper
    <>
      <meta property="og:title" content="How make search good?" />
      <meta property="og:image" content="/images/hmsg/hmsg.png" />
      <meta
        property="og:description"
        content="Evaluating documentation search for coding agents. The shift from traditional recall/precision metrics and its impact on coding agent efficiency."
      />
    </>
  )
}
